"use client";

import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import Image from "next/image";
import { useState } from "react";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/ValueAddedSolutions/industrial-automation/CaseStudy";

const CASES = [
	{
		keyPrefix: "caseStudy1",
		image: `${IMAGE_BASE}/flexible-manufacturing-ultra-fast-delivery-for-3c-electronics-module-brackets.webp`,
	},
	{
		keyPrefix: "caseStudy2",
		image: `${IMAGE_BASE}/precision-manufacturing-flexible-adaptation-for-new-energy-vehicle-connector-components.webp`,
	},
] as const;

const SOLUTION_ITEMS = [1, 2, 3] as const;

const CheckIcon = () => (
	<span
		className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-themePrimary400 text-white"
		aria-hidden="true"
	>
		<svg viewBox="0 0 16 16" className="h-3 w-3" fill="none">
			<path
				d="M3.5 8.3 6.4 11 12.5 5"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	</span>
);

export default function IndustrialAutomationCaseStudy() {
	const t = useTranslations("new-industrial-automation");
	const [activeIndex, setActiveIndex] = useState(0);
	const activeCase = CASES[activeIndex];

	const goPrevious = () => {
		setActiveIndex((current) => (current === 0 ? CASES.length - 1 : current - 1));
	};

	const goNext = () => {
		setActiveIndex((current) => (current === CASES.length - 1 ? 0 : current + 1));
	};

	return (
		<section className="bg-[#eeeeee] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container relative">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("caseStudyTitle")}
					</h2>
				</header>

				<div>
					<div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-10 lg:px-14">
						<div className="relative aspect-[4/3] bg-[#ccc]">
							<Image
								src={activeCase.image}
								alt={t(`${activeCase.keyPrefix}ImageAlt`)}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>

						<article>
							<h3 className="mb-5 text-xl font-bold leading-tight text-black sm:text-2xl">
								{t(`${activeCase.keyPrefix}Title`)}
							</h3>

							<div className="space-y-5">
								<div>
									<h4 className="mb-2 text-base font-bold text-black sm:text-lg">
										{t("caseStudyChallenge")}
									</h4>
									<p className="text-sm leading-relaxed text-black md:text-base">
										{t(`${activeCase.keyPrefix}Challenge`)}
									</p>
								</div>

								<div>
									<h4 className="mb-2 text-base font-bold text-black sm:text-lg">{t("caseStudySolution")}</h4>
									<p className="mb-2 text-sm leading-relaxed text-black md:text-base">
										{t(`${activeCase.keyPrefix}SolutionIntro`)}
									</p>
									<ul className="space-y-2">
										{SOLUTION_ITEMS.map((item) => (
											<li key={item} className="flex gap-2 text-sm leading-relaxed text-black md:text-base">
												<CheckIcon />
												<span>{t(`${activeCase.keyPrefix}Solution${item}`)}</span>
											</li>
										))}
									</ul>
								</div>

								<div>
									<h4 className="mb-2 text-base font-bold text-black sm:text-lg">{t("caseStudyResult")}</h4>
									<p className="text-sm leading-relaxed text-black md:text-base">
										{t(`${activeCase.keyPrefix}Result`)}
									</p>
								</div>
							</div>
						</article>
					</div>

					<div className="mt-8 flex justify-center gap-4 lg:pointer-events-none lg:absolute lg:inset-y-0 lg:left-0 lg:right-0 lg:mt-0 lg:items-center lg:justify-between">
						<button
							type="button"
							onClick={goPrevious}
							aria-label={t("caseStudyPrevious")}
							className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full text-themePrimary400"
						>
							<CaretLeft size={42} weight="bold" aria-hidden />
						</button>
						<button
							type="button"
							onClick={goNext}
							aria-label={t("caseStudyNext")}
							className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full text-themePrimary400"
						>
							<CaretRight size={42} weight="bold" aria-hidden />
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}
